import React from 'react';
import { Box, Stack } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ApolloQueryResult } from '@apollo/client';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { CobaltIntelligenceWidget } from 'modules/integrations/cobaltIntelligence';
import {
  CreditLineCard,
  HealthCheckCard,
  DocumentsCard,
  TasksCard,
  VendorOpportunity,
} from 'modules/vendorOpportunity';
import { ScoreCardV2 } from '../../ScoreCardV2';
import { ScoreCardFastTrack } from 'pages/VendorOpportunity/ScoreCardFastTrack';

interface Props {
  vo: VendorOpportunity;
  creditApp: any;
  userName: string;
  accountId: string;
  vendorProfile: any;
  creditAppRefetch: () => Promise<ApolloQueryResult<void>>;
  permissions: Record<'cobaltIntelligence' | 'creditLine' | 'healthCheck' | 'docs' | 'tasks', boolean>;
  contentViewType?: any;
  refetchVO?: any;
  equipmentData?: any;
  refetchEquipment?: any;
  equipmentLoading?: boolean;
  userProfile?: any;
  account?: any;
  portalConfigurationDataLoading?: boolean;
  creditAppOwners?: any;
  isScorecardHiddenForUserRole?: boolean;
  showCreditLineCard?: boolean;
}

export const CreditLineTab = ({
  vo,
  userName,
  accountId,
  creditApp,
  permissions,
  vendorProfile,
  creditAppRefetch,
  contentViewType,
  refetchVO,
  equipmentData,
  refetchEquipment,
  equipmentLoading,
  userProfile,
  account,
  portalConfigurationDataLoading,
  creditAppOwners,
  isScorecardHiddenForUserRole,
  showCreditLineCard,
}: Props) => {
  return (
    <Stack gap="1rem">
      <RenderWithCondition condition={permissions.creditLine}>
        <CreditLineCard
          userName={userName}
          isCreditLineRequested={vo.creditLineRequest?.length > 0 || creditApp?.applyingFor?.includes('creditLine')}
          creditLineRequest={vo?.creditLineRequest?.[vo?.creditLineRequest.length - 1] || null}
        />
      </RenderWithCondition>

      <RenderWithCondition condition={!!vo.fastTrackResults}>
        <ScoreCardFastTrack vo={vo} />
      </RenderWithCondition>

      <RenderWithCondition condition={!isScorecardHiddenForUserRole && (showCreditLineCard ?? false)}>
        <ScoreCardV2
          contentViewType={contentViewType}
          vo={vo}
          cs={null}
          refetchVO={refetchVO}
          equipmentData={equipmentData}
          refetchEquipment={refetchEquipment}
          equipmentLoading={equipmentLoading}
          creditApplication={creditApp}
          vp={vendorProfile}
          userProfile={userProfile}
          account={account}
          refetchCreditApp={creditAppRefetch}
          portalConfigurationDataLoading={portalConfigurationDataLoading}
          creditAppOwners={creditAppOwners}
        />
      </RenderWithCondition>

      <RenderWithCondition condition={permissions.healthCheck && vendorProfile}>
        <HealthCheckCard
          userName={userName}
          creditApp={creditApp}
          cardTitle="Compliance"
          accountId={accountId}
          refetchCreditApp={creditAppRefetch}
          softPullInDealerPortal={
            vendorProfile ? vendorProfile.softPullInDealerPortal : 'CBC_EQUIFAX_SOFT_SUMMARY_ONLY'
          }
        />
      </RenderWithCondition>

      <RenderWithCondition condition={permissions.docs}>
        <DocumentsCard VOId={vo.vendorOpportunityId} />
      </RenderWithCondition>

      <RenderWithCondition condition={permissions.tasks}>
        <TasksCard VOId={vo.vendorOpportunityId} />
      </RenderWithCondition>

      <RenderWithCondition condition={permissions.cobaltIntelligence}>
        <CobaltIntelligenceWidget voId={vo.vendorOpportunityId} />
      </RenderWithCondition>
    </Stack>
  );
};
